import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { PageHero, Section } from "@/components/layout/Page";
import { EventPhotoGallery } from "@/components/EventPhotoGallery";
import { EventCountdown } from "@/components/EventCountdown";
import { ExpoHallMap } from "@/components/visuals/ExpoHallMap";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/I18nContext";
import { getLocalizedMeta, metaTags } from "@/lib/utils/seo";
import { FaqSection } from "@/components/layout/FaqSection";
import { faqHeadScript } from "@/lib/utils/faq-schema";
import { SUBMISSION_SOURCES } from "@/lib/submission-context";
import { trackNewsletterSignup } from "@/lib/analytics-events";
import { TurnstileWidget, type TurnstileWidgetRef } from "@/components/ui/TurnstileWidget";
import { TURNSTILE_ACTIONS } from "@/lib/api/turnstile";
import { getEventBySlug, otherEvents, type TradeEvent } from "@/lib/mock/events";
import { EVENT_MEDIA } from "@/lib/mock/event-media";
import { localizedUrl } from "@/lib/utils/site-url";
import { translateStoreSsr } from "@/lib/utils/store-i18n";
import { ORGANIZATION_ID } from "@/routes/__root";
import { COMPANY } from "@/lib/company";
import expoHero from "@/assets/optimized/aegean-fish-cages-1024.webp";
import {
  CalendarDays,
  MapPin,
  Building2,
  Ticket,
  ArrowRight,
  Fish,
  Mail,
  Sparkles,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import aquariumImage from "@/assets/optimized/artemia-in-spoon-1024.webp";
import sv12HeroImg from "@/assets/products/optimized/super-fresh-chlorella-sv12-1024.webp";
import { PRODUCTS } from "@/lib/mock/products";
import { publishNotFound } from "@/lib/utils/canonical-override";

const EXPO_FEATURED_SLUGS = [
  "decapsulated-dry-artemia-cysts",
  "artemia",
  "enzyfish-gold",
  "emerald-chlorella-powder",
] as const;

export const Route = createFileRoute("/events/$slug")({
  loader: ({ params }) => {
    const event = getEventBySlug(params.slug);
    if (!event) {
      publishNotFound(`/events/${encodeURIComponent(params.slug)}`);
      throw notFound();
    }
    return { event };
  },
  head: ({ loaderData, params }) => {
    const EVENT = loaderData?.event;
    if (!EVENT) return {};
    const url = localizedUrl(`/events/${params.slug}`);
    const eventSchema = {
      "@context": "https://schema.org",
      "@type": "ExhibitionEvent",
      "@id": `${url}#event`,
      name: translateStoreSsr(`events.${EVENT.id}.title`, EVENT.id),
      startDate: `${EVENT.startDate}T10:00:00+03:00`,
      endDate: `${EVENT.endDate}T18:00:00+03:00`,
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      url,
      location: {
        "@type": "Place",
        name: EVENT.venue ?? EVENT.city,
        address: {
          "@type": "PostalAddress",
          addressLocality: EVENT.city,
          addressCountry: EVENT.country === "Türkiye" ? "TR" : EVENT.country,
        },
      },
      organizer: {
        "@type": "Organization",
        name: translateStoreSsr(`events.${EVENT.id}.organizer`, ""),
        url: EVENT.externalHref,
      },
      ...(EVENT.participation === "exhibitor" ? { performer: { "@id": ORGANIZATION_ID } } : {}),
    };

    return {
      meta: EVENT.seoPage
        ? getLocalizedMeta(EVENT.seoPage)
        : metaTags(
            `${translateStoreSsr(`events.${EVENT.id}.title`, EVENT.id)} | VARS`,
            translateStoreSsr(`events.${EVENT.id}.body`, ""),
          ),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(eventSchema) },
        ...(EVENT.hasHallMap ? [faqHeadScript("expo", `/events/${params.slug}`)] : []),
      ],
    };
  },
  component: ExpoPage,
});

function ExpoPage() {
  const { t, lang } = useI18n();

  const [inviteToken, setInviteToken] = useState("");
  const inviteTurnstileRef = useRef<TurnstileWidgetRef>(null);
  const { event: EVENT } = Route.useLoaderData() as { event: TradeEvent };

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteConsent, setInviteConsent] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);

  const tp = (slug: string, field: string, fallback: string): string => {
    const key = `product.${slug}.${field}`;
    const res = t(key);
    return res === key ? fallback : res;
  };

  const tpMetric = (
    slug: string,
    idx: number,
    part: "label" | "value",
    fallback: string,
  ): string => {
    const key = `product.${slug}.metrics.${idx}.${part}`;
    const res = t(key);
    return res === key ? fallback : res;
  };

  const featuredProducts = EXPO_FEATURED_SLUGS.map((slug) =>
    PRODUCTS.find((p) => p.slug === slug),
  ).filter((p): p is NonNullable<typeof p> => Boolean(p));

  /**
   * The spotlight heading was hardcoded as "Super Fresh Chlorella V12 (SV-12)",
   * which disagrees with the name registry in
   * docs/project/localization/glossary.md (and with products.ts). Read it from
   * the product record so the fair page cannot drift from the binding name.
   */
  const sv12 = PRODUCTS.find((p) => p.slug === "super-fresh-chlorella-v12");

  const submitInvite = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!inviteConsent) {
      toast.error(t("expo.invite.needConsent"));
      return;
    }
    // Same: the token goes if there is one, and a missing one no longer
    // costs the signup.
    setInviteBusy(true);
    try {
      const response = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: inviteEmail, turnstileToken: inviteToken }),
      });
      if (!response.ok) {
        // Deliberately NOT surfacing `body.message`. It is backend phrasing,
        // written for a log rather than for a purchasing manager, and it is not
        // localized — so a Turkish visitor got an English validation string.
        // The page owns its own failure copy.
        await response.json().catch(() => ({}));
        throw new Error();
      }
      trackNewsletterSignup({ formSource: SUBMISSION_SOURCES.newsletter, locale: lang });
      toast.success(t("expo.invite.done"), { description: t("expo.invite.doneBody") });
      inviteTurnstileRef.current?.reset();
      setInviteEmail("");
      setInviteConsent(false);
    } catch {
      inviteTurnstileRef.current?.reset();
      // Always the page's own copy. Our own throws carry no message, and a
      // network rejection carries "Failed to fetch" — neither is something to
      // show a purchasing manager, and neither is localized.
      toast.error(t("expo.invite.failed"));
    } finally {
      setInviteBusy(false);
    }
  };

  const rich = Boolean(EVENT.hasHallMap);
  const isPast = EVENT.endDate < new Date().toISOString().slice(0, 10);
  const media = EVENT_MEDIA[EVENT.id];
  const heroImage = EVENT.id === "bisfe" ? media?.photos[0]?.src : expoHero;
  const title = rich ? t("expo.hero.title") : t(`events.${EVENT.id}.title`);
  const description = rich ? t("expo.hero.description") : t(`events.${EVENT.id}.body`);
  const eyebrow = rich ? t("expo.hero.eyebrow") : t(`events.${EVENT.id}.date`);

  const facts = [
    { icon: CalendarDays, label: t("expo.facts.dates.label"), value: t(`events.${EVENT.id}.date`) },
    {
      icon: MapPin,
      label: t("expo.facts.venue.label"),
      value: EVENT.venue ? `${EVENT.venue}, ${EVENT.city}` : `${EVENT.city}, ${EVENT.country}`,
    },
    ...(EVENT.hall
      ? [
          {
            icon: Building2,
            label: t("expo.facts.hall.label"),
            value: `${t("expo.facts.hall.value")} ${EVENT.hall}`,
          },
        ]
      : []),
    ...(EVENT.stand
      ? [{ icon: Ticket, label: t("expo.facts.stand.label"), value: EVENT.stand }]
      : []),
  ];

  const topics = ["portfolio", "network", "reach", "support"] as const;

  /**
   * "Other fairs we attend" was advertising a 2024 expo and a 2025 conference
   * and nothing else, three days before this fair opened — a present-tense
   * heading over a list of finished events, sitting in the middle of the
   * conversion path. Upcoming fairs keep that heading and lead; finished ones
   * are labelled as history and follow, most recent first. The whole block now
   * renders after the FAQ, out of the funnel.
   */
  const others = otherEvents(EVENT.id);
  const today = new Date().toISOString().slice(0, 10);
  const upcomingOthers = others.filter((e) => e.endDate >= today);
  const pastOthers = others.filter((e) => e.endDate < today).reverse();
  const otherGroups = [
    { key: "upcoming", heading: t("events.otherEvents"), events: upcomingOthers },
    { key: "past", heading: t("events.pastEvents"), events: pastOthers },
  ].filter((group) => group.events.length > 0);

  return (
    <>
      <PageHero
        image={heroImage ?? expoHero}
        imageWidth={EVENT.id === "bisfe" ? 1200 : 1024}
        imageHeight={EVENT.id === "bisfe" ? 395 : 576}
        eyebrow={eyebrow}
        title={title}
        description={description}
      >
        <div className="flex flex-wrap gap-3">
          {/* Built as a plain anchor, matching its siblings below, and NOT as
              <Button variant="outline">. That variant carries `bg-background`,
              which is white; adding `text-white` on top of it rendered this
              label white-on-white — an invisible button in the middle of the
              hero. Any outline Button dropped onto a dark surface has the same
              problem, so the dark-surface pattern lives here as an anchor. */}
          <a
            href="#hall-map"
            className="inline-flex items-center gap-2 rounded-xl border border-white/25 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint"
          >
            <MapPin className="h-4 w-4" aria-hidden="true" />
            {t("expo.hero.cta.findStand")}
          </a>
          {EVENT.externalHref && (
            <a
              href={EVENT.externalHref}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-2 rounded-xl border border-white/25 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint"
            >
              {isPast ? t("events.organiserSite") : t("expo.hero.cta.register")}
            </a>
          )}
          {EVENT.participationHref && (
            <a
              href={EVENT.participationHref}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-2 rounded-xl border border-white/25 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint"
            >
              {t("events.officialParticipation")}
            </a>
          )}
        </div>
      </PageHero>

      {/* The one thing this page could not say: that the doors open soon.
          Client-rendered, so it self-updates without a redeploy, and it drops
          out entirely once the fair has closed. */}
      {rich && !isPast && <EventCountdown event={EVENT} showCta={false} showFacts={false} />}

      <Section className="py-10">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((f) => (
            <div key={f.label} className="card-flat flex items-start gap-3 p-5">
              <f.icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  {f.label}
                </div>
                <div className="font-display text-lg font-bold text-navy">{f.value}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {rich && (
        <Section band>
          <div className="grid gap-10 lg:grid-cols-2 lg:items-start">
            <div>
              <div className="mb-4 inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                <span className="inline-block h-[2px] w-8 bg-primary" />
                {t("expo.about.eyebrow")}
              </div>
              <h2 className="font-display text-3xl font-bold text-navy md:text-4xl">
                {t("expo.about.title")}
              </h2>
              <div className="mt-5 space-y-4 text-base leading-relaxed text-muted-foreground">
                <p>{t("expo.about.p1")}</p>
                <p>{t("expo.about.p2")}</p>
              </div>
            </div>

            <div className="glass-card space-y-4 rounded-3xl border border-border/80 bg-background p-7 shadow-md">
              <h3 className="font-display text-xl font-bold text-navy">{t("expo.topics.title")}</h3>
              <ul className="space-y-3">
                {topics.map((topic) => (
                  <li key={topic} className="flex gap-3">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <div>
                      <div className="text-sm font-bold text-navy">
                        {t(`expo.topics.${topic}.title`)}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {t(`expo.topics.${topic}.body`)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
              {/* No CTA in this card. It used to hold a third button to
                  `#claim-discount` — VARS-137 gave it a different label than the
                  hero to stop seven identical buttons, which fixed the wording
                  and left the redundancy. With the discount claim named as the
                  page's one primary outcome, three buttons to one anchor is two
                  too many: the hero opens the page with it and the closing block
                  ends with it. This card's job is to say what we are bringing. */}
            </div>
          </div>
        </Section>
      )}

      {/* Interactive Hall C Floor Plan (Light Theme & Zoomed Out) */}
      {EVENT.hasHallMap && (
        <Section className="py-8">
          <ExpoHallMap />
        </Section>
      )}

      {/* Featured Products & SV-12 Showcase */}
      {EVENT.hasHallMap && (
        <Section band className="py-14">
          <div className="mb-10 max-w-3xl">
            <div className="mb-3 inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              <span className="inline-block h-[2px] w-8 bg-primary" />
              {t("expo.products.eyebrow")}
            </div>
            <h2 className="font-display text-3xl font-bold text-navy md:text-4xl">
              {t("expo.products.title")}
            </h2>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              {t("expo.products.description")}
            </p>
          </div>

          {/* SV-12 Live Microalgae Spotlight Hero Card */}
          <div className="relative isolate overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-mint/15 via-background to-surface-alt/70 p-6 sm:p-8 md:p-10 shadow-xl mb-12">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -right-24 -top-24 -z-10 h-72 w-72 rounded-full bg-mint/20 blur-3xl"
            />
            <div className="grid gap-8 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-7 space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 border border-primary/20 px-3 py-1 text-xs font-bold text-primary">
                    <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                    {t("expo.sv12.badge")}
                  </span>
                  <span className="rounded-full bg-navy/5 border border-navy/10 px-3 py-1 text-xs font-medium text-navy/70">
                    {t("expo.sv12.origin")}
                  </span>
                </div>

                <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-navy leading-tight">
                  {sv12 ? tp(sv12.slug, "title", sv12.title) : t("expo.sv12.badge")}
                </h3>

                <p className="text-sm sm:text-base text-navy/85 leading-relaxed">
                  {t("expo.sv12.tagline")}
                </p>

                {/* 4 Key Technical Metrics */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                  {[1, 2, 3, 4].map((idx) => (
                    <div
                      key={idx}
                      className="rounded-2xl border border-border/80 bg-background/90 p-3 shadow-xs"
                    >
                      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        {t(`expo.sv12.spec${idx}.label`)}
                      </div>
                      <div className="font-display text-sm sm:text-base font-bold text-primary mt-0.5">
                        {t(`expo.sv12.spec${idx}.value`)}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-3 pt-3">
                  <Button
                    asChild
                    size="lg"
                    className="rounded-xl font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-md"
                  >
                    <Link
                      to="/products/$category/$slug"
                      params={{
                        category: "live-feed-aquaculture",
                        slug: "super-fresh-chlorella-v12",
                      }}
                    >
                      {t("expo.sv12.ctaSpecs")}
                      <ArrowRight className="ms-1.5 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Packshot */}
              <div className="lg:col-span-5 flex justify-center">
                <div className="relative w-full max-w-sm rounded-3xl border border-border/80 bg-navy/[0.03] p-6 shadow-inner text-center">
                  <img
                    src={sv12HeroImg}
                    alt="Super Fresh Chlorella SV-12 live liquid microalgae concentrate 20-litre insulated cold-chain case"
                    width={800}
                    height={800}
                    loading="lazy"
                    decoding="async"
                    className="mx-auto h-56 sm:h-64 object-contain drop-shadow-xl transition-transform duration-500 hover:scale-105"
                  />
                  <div className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-background/90 px-3 py-1.5 text-xs font-bold text-navy/80 border border-border/60 shadow-xs">
                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span>{t("expo.sv12.packFormat")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Exhibition Product Cards Grid */}
          {/* Four featured products in a 3-up grid left an orphan on row two
              beside a two-thirds hole. 2-up then 4-up fills both rows. */}
          <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
            {featuredProducts.map((p) => (
              <div
                key={p.slug}
                className="group relative flex flex-col rounded-3xl border border-border/80 bg-background shadow-md overflow-hidden transition-all duration-300 hover:border-primary/50 hover:shadow-xl"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-surface-alt/70 p-4 flex items-center justify-center">
                  {p.thumbnail || p.image ? (
                    <img
                      src={p.thumbnail ?? p.image}
                      alt={tp(p.slug, "title", p.title)}
                      loading="lazy"
                      decoding="async"
                      width={600}
                      height={400}
                      className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="text-3xl font-display font-bold text-navy/20">
                      {p.title.slice(0, 2)}
                    </div>
                  )}

                  <div className="absolute top-3 start-3">
                    <span className="inline-flex items-center gap-1 rounded-full bg-navy/90 text-white px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider backdrop-blur border border-white/10 shadow-xs">
                      {t(`cat.${p.category}`)}
                    </span>
                  </div>

                  <div className="absolute top-3 end-3 flex flex-col items-end gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full bg-mint/90 text-navy px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider shadow-xs">
                      {t("expo.products.standBadge")}
                    </span>
                  </div>
                </div>

                <div className="flex flex-1 flex-col p-6 space-y-3">
                  <div>
                    <h3 className="font-display text-lg font-bold text-navy group-hover:text-primary transition-colors leading-snug">
                      <Link
                        to="/products/$category/$slug"
                        params={{ category: p.category, slug: p.slug }}
                      >
                        {tp(p.slug, "title", p.title)}
                      </Link>
                    </h3>
                    {p.latinName && (
                      <p className="text-xs italic text-primary/80 mt-0.5 font-medium">
                        {p.latinName}
                      </p>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 flex-1">
                    {tp(p.slug, "tagline", p.tagline)}
                  </p>

                  {/* 2 Key Metrics */}
                  {p.metrics && p.metrics.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      {p.metrics.slice(0, 2).map((m, mi) => (
                        <div
                          key={m.label}
                          className="rounded-xl border border-border/60 bg-surface-alt/60 p-2 text-center"
                        >
                          <div className="font-display text-xs font-bold text-navy truncate">
                            {tpMetric(p.slug, mi, "value", m.value)}
                          </div>
                          <div className="text-[10px] text-muted-foreground font-semibold truncate">
                            {tpMetric(p.slug, mi, "label", m.label)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Clean Specification CTA */}
                  <div className="pt-3 border-t border-border/60 flex items-center gap-2">
                    <Button
                      asChild
                      size="sm"
                      className="flex-1 rounded-xl text-xs font-bold gap-1 bg-primary text-primary-foreground hover:bg-primary/90 shadow-xs"
                    >
                      <Link
                        to="/products/$category/$slug"
                        params={{ category: p.category, slug: p.slug }}
                      >
                        <span>{t("expo.products.viewProduct")}</span>
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Button asChild variant="outline" size="lg" className="rounded-xl font-bold">
              <Link to="/products">
                {t("expo.products.viewAll")}
                <ArrowRight className="ms-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </Section>
      )}

      {/* Recap gallery */}
      {media && (
        <Section band>
          <div className="mb-8 max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              <span className="inline-block h-[2px] w-8 bg-primary" />
              {t("events.recap.heading")}
            </div>
            <h2 className="font-display text-3xl font-bold text-navy md:text-4xl">
              {t(`events.${EVENT.id}.title`)}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">{t("events.recap.body")}</p>
          </div>
          <EventPhotoGallery
            photos={media.photos.map((photo) => ({
              src: photo.src,
              alt: t(photo.captionKey),
            }))}
          />
        </Section>
      )}

      {/* Hobbyists and shops section */}
      <Section className="py-12">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="relative overflow-hidden rounded-3xl border border-border/80 shadow-md">
            <img
              src={aquariumImage}
              alt={t("expo.hobby.imageAlt")}
              loading="lazy"
              decoding="async"
              width={1024}
              height={576}
              className="h-64 w-full object-cover md:h-80"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
          </div>

          <div>
            <div className="mb-4 inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              <span className="inline-block h-[2px] w-8 bg-primary" />
              {t("expo.hobby.eyebrow")}
            </div>
            <h2 className="font-display text-3xl font-bold text-navy">{t("expo.hobby.title")}</h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              {t("expo.hobby.body")}
            </p>
            <ul className="mt-5 space-y-2.5">
              {(["decap", "chlorella", "retail"] as const).map((item) => (
                <li key={item} className="flex gap-3 text-sm text-muted-foreground">
                  <Fish className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  {t(`expo.hobby.${item}`)}
                </li>
              ))}
            </ul>
            <Button asChild variant="outline" className="mt-6 rounded-xl font-bold">
              <Link to="/aquariums-and-hobbyists">
                {t("expo.hobby.cta")}
                <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>
      </Section>

      {/* Invitation list */}
      <Section className="pb-14 pt-0">
        <div className="glass-card mx-auto max-w-3xl rounded-3xl border border-border/80 bg-surface-alt/70 p-8 text-center shadow-md">
          <Mail className="mx-auto h-8 w-8 text-primary" aria-hidden="true" />
          <h2 className="mt-3 font-display text-2xl font-bold text-navy">
            {t("expo.invite.title")}
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
            {t("expo.invite.body")}
          </p>

          <form onSubmit={submitInvite} className="mx-auto mt-5 max-w-md space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row">
              <Input
                type="email"
                required
                value={inviteEmail}
                onChange={(event) => setInviteEmail(event.target.value)}
                placeholder={t("expo.invite.placeholder")}
                aria-label={t("expo.invite.placeholder")}
                className="h-11 rounded-xl bg-background text-sm"
              />

              <Button
                type="submit"
                disabled={inviteBusy}
                className="h-11 shrink-0 rounded-xl px-6 font-bold"
              >
                {inviteBusy ? t("expo.invite.sending") : t("expo.invite.cta")}
              </Button>
            </div>

            <TurnstileWidget
              ref={inviteTurnstileRef}
              action={TURNSTILE_ACTIONS.newsletter}
              onSuccess={setInviteToken}
              onExpire={() => setInviteToken("")}
            />

            <label className="flex items-start gap-1.5 text-start text-[11px] leading-relaxed text-muted-foreground">
              {/* 14px was the smallest interactive target on the site. Same
                  24x24 treatment as the claim form's consent box. */}
              <span className="grid h-6 w-6 shrink-0 place-items-center">
                <input
                  type="checkbox"
                  checked={inviteConsent}
                  onChange={(event) => setInviteConsent(event.target.checked)}
                  className="h-4 w-4 rounded border-border focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                />
              </span>
              <span className="pt-1">
                {t("expo.invite.consent")}{" "}
                <Link to="/privacy" className="font-semibold text-primary hover:underline">
                  {t("expo.invite.privacy")}
                </Link>
              </span>
            </label>
          </form>
        </div>
      </Section>

      <Section band className="py-14">
        <div className="mx-auto max-w-3xl space-y-4 text-center">
          <h2 className="font-display text-2xl font-bold text-navy md:text-3xl">
            {t("expo.visit.title")}
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{t("expo.visit.body")}</p>
          <p className="text-xs text-muted-foreground">
            {COMPANY.legalName} — {COMPANY.phone} — {COMPANY.email}
          </p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            <Button asChild size="lg" className="rounded-xl font-bold">
              <Link to="/request-quote">{t("nav.quote")}</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl font-bold">
              <Link to="/contactus">{t("expo.visit.cta.contact")}</Link>
            </Button>
          </div>
        </div>
      </Section>

      {EVENT.hasHallMap && <FaqSection page="expo" />}

      {otherGroups.length > 0 && (
        <Section className={EVENT.hasHallMap ? "py-12" : undefined}>
          <div className="space-y-10">
            {otherGroups.map((group) => (
              <div key={group.key}>
                <h2 className="mb-6 font-display text-2xl font-bold text-navy">{group.heading}</h2>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {group.events.map((other) => (
                    <Link
                      key={other.id}
                      to="/events/$slug"
                      params={{ slug: other.slug }}
                      className="card-flat group block p-5 transition-all hover:border-primary/60 hover:shadow-lg"
                    >
                      <div className="flex flex-wrap items-center gap-2 text-xs font-bold">
                        <span className="inline-flex items-center gap-1 text-primary">
                          <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                          {other.city}, {other.country}
                        </span>
                        <span className="text-muted-foreground">
                          {t(`events.${other.id}.date`)}
                        </span>
                      </div>
                      <h3 className="mt-2 font-display text-lg font-bold text-navy">
                        {t(`events.${other.id}.title`)}
                      </h3>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                        {t(`events.${other.id}.body`)}
                      </p>
                      <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-primary">
                        {t("events.viewDetails")}
                        <ArrowRight
                          className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
                          aria-hidden="true"
                        />
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </>
  );
}
